import { createContext, useContext } from 'react';

/**
 * The minimal workspace/tenant shape the admin template's chrome reads from.
 * Only relevant when `templateConfig.tenancy === 'enabled'`.
 */
export interface Workspace {
  id: string;
  name: string;
  /** Optional URL slug. Falls back to a slugified version of `name`. */
  slug?: string | null;
}

export interface WorkspaceContextValue {
  /** All workspaces the current user has access to. */
  workspaces: Workspace[];
  /** The currently active workspace, or null if the user has none. */
  activeOrganization: Workspace | null;
  activeOrganizationId: string | null;
  loading: boolean;
  /** Persist the new active workspace and revalidate any consuming queries. */
  switchWorkspace: (id: string) => Promise<void> | void;
  /** Refetch the workspace list (e.g., after accepting an invitation). */
  refreshWorkspaces: () => Promise<void> | void;
}

/**
 * When tenancy is disabled in the config, the template renders without a workspace
 * switcher and never calls into this context. Consumers can ignore it entirely.
 */
export const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) {
    throw new Error(
      '[catedge-admin-template] useWorkspace() was called outside a <WorkspaceContext.Provider>. ' +
        'If your app is multi-tenant, provide a workspace context. If not, set ' +
        '`tenancy: "disabled"` in template.config.ts and the template will skip this call.'
    );
  }
  return value;
}
